import {
    Component, Input, OnInit, OnChanges, SimpleChanges,
    ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, Output, EventEmitter
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { LayoutUtilsService, MessageType } from 'src/app/_theme/core/_base/curd/utils/layout-utils.service';
import { environment } from 'src/environments/environment';
import { getStatusPageErrorMessage } from 'src/app/_theme/core/_base/curd/utils/validators-util';
import {
    AccessControl, AccessEntry, AvailableGroup, AvailableUser,
    ServiceAccessPermission, ServiceAccessResponse
} from '../../models/service.models';

@Component({
    selector: 'app-service-access-control',
    templateUrl: './service-access-control.component.html',
    styleUrls: [
        './service-access-control.component.scss',
        './service-access-control.component.entries-ownership.scss',
        './service-access-control.component.tabs-add-panel.scss',
        './service-access-control.component.dark-overrides.scss',
        './service-access-control.component.dark-overrides-extra.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatRadioModule,
        MatSlideToggleModule,
        MatTooltipModule,
        NgxSkeletonLoaderModule
    ]
})
export class ServiceAccessControlComponent implements OnInit, OnChanges {
    @Input() serviceId!: string;
    @Input() token!: string;
    @Input() entityType: 'service' | 'group' | 'statusPage' = 'service';
    /** When true, all edit interactions are blocked regardless of canModifyAccess. */
    @Input() readonly: boolean = false;
    /** When false, keep permission resolution but suppress the Custom Access UI entirely. */
    @Input() showCustomAccessUi: boolean = true;
    /** When true, add-panel lists use checkbox-only tab focus (row remains mouse-clickable). */
    @Input() checkboxOnlyEntityTabFocus: boolean = false;
    /** Optional preloaded permission supplied by parent to avoid duplicate permission API calls. */
    @Input() permissionData?: ServiceAccessPermission | null;
    /** Optional loading flag for preloaded permission mode. */
    @Input() permissionLoading: boolean | null = null;
    /** Emits whether base service fields (name/description) can be edited. */
    @Output() editPermissionChange = new EventEmitter<boolean>();
    /** Requests parent to refresh permission when a permission-changing action completes. */
    @Output() permissionRefreshRequested = new EventEmitter<void>();

    isLoadingAccess = false;
    hasAccess = false;
    /** Owner-only permission for Custom Access actions. */
    canModifyAccess = false;

    accessForm: AccessControl = {
        enabled: false,
        entries: [],
        removedEntries: []
    };
    accessModified = false;

    availableUsers: AvailableUser[] = [];
    availableGroups: AvailableGroup[] = [];

    activeAccessTab: 'users' | 'groups' = 'users';
    activePermissionTab: 'edit' | 'view' = 'edit';

    searchQueryUsers = '';
    searchQueryGroups = '';
    searchQueryTransfer = '';

    isEditingAccess = false;
    isEditingEntries = false;
    isLoadingEntities = false;
    isTransferringOwnership = false;
    selectedNewOwnerId: string | number | null = null;
    pendingOwnerTransfer: { id: string | number; name: string } | null = null;

    selectedUserIds = new Set<string | number>();
    selectedGroupIds = new Set<string | number>();
    selectedEntryIds = new Set<string | number>();

    permissionOptions = [
        { label: 'Owner', value: 1 },
        { label: 'Edit', value: 2 },
        { label: 'View', value: 3 }
    ];

    readonly accessEntrySkeletonWidths = ['68%', '54%', '72%'];
    readonly addPanelEntitySkeletonWidths = ['74%', '63%', '69%', '57%'];
    private lastLoadKey: string | null = null;

    constructor(
        private readonly statusPageService: StatusPageService,
        private readonly cdr: ChangeDetectorRef,
        private readonly layoutUtilsService: LayoutUtilsService
    ) { }

    ngOnInit(): void {
        if (this.serviceId) {
            this.loadAccessDependencies();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasReloadInputChange = ['serviceId', 'token', 'entityType', 'showCustomAccessUi']
            .some((inputKey) => !!changes[inputKey] && !changes[inputKey].firstChange);
        const hasPermissionInputChange = ['permissionData', 'permissionLoading']
            .some((inputKey) => !!changes[inputKey] && !changes[inputKey].firstChange);

        if (!hasReloadInputChange && !(hasPermissionInputChange && this.usesPreloadedPermission())) {
            return;
        }

        if (hasReloadInputChange) {
            this.reset();
        }
        this.loadAccessDependencies(true);
    }

    loadAccessDependencies(forceReload: boolean = false): void {
        if (!this.serviceId || !this.token) return;

        const loadKey = this.buildLoadKey();
        if (!forceReload && this.lastLoadKey === loadKey) {
            return;
        }
        this.lastLoadKey = loadKey;

        if (this.usesPreloadedPermission()) {
            if (this.permissionLoading === true && this.permissionData == null) {
                this.isLoadingAccess = true;
                this.cdr.detectChanges();
                return;
            }
            this.applyPermissionData(this.permissionData ?? null);
            return;
        }

        this.isLoadingAccess = true;
        this.cdr.detectChanges();

        this.statusPageService.getServiceAccessPermission(this.token, this.serviceId, this.entityType)
            .pipe(catchError(() => of(null)))
            .subscribe({
                next: (permissionData: any) => {
                    this.applyPermissionData(permissionData ?? null);
                },
                error: () => {
                    this.editPermissionChange.emit(false);
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                }
            });
    }

    loadAvailableEntities(): void {
        if (!this.serviceId || !this.token) return;
        this.isLoadingEntities = true;
        this.availableUsers = [];
        this.availableGroups = [];
        this.cdr.detectChanges();

        forkJoin({
            users: this.statusPageService.getAvailableUsers(this.token, this.serviceId, this.entityType).pipe(catchError(() => of([]))),
            groups: this.statusPageService.getAvailableGroups(this.token, this.serviceId, this.entityType).pipe(catchError(() => of([])))
        }).subscribe({
            next: (results: any) => {
                const { users: rawUsers, groups: rawGroups } = results;
                this.availableUsers = (rawUsers ?? []).map((u: any) => ({
                    id: u.userId,
                    name: u.fullName,
                    entitlement: u.entitlement
                }));
                this.availableGroups = (rawGroups ?? []).map((g: any) => ({
                    id: g.groupId,
                    name: g.groupName
                }));
                this.isLoadingEntities = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoadingEntities = false;
                this.cdr.detectChanges();
            }
        });
    }

    toggleAddPanel(): void {
        this.isEditingAccess = !this.isEditingAccess;
        if (this.isEditingAccess) {
            this.selectedUserIds.clear();
            this.selectedGroupIds.clear();
            this.searchQueryUsers = '';
            this.searchQueryGroups = '';
            this.loadAvailableEntities();
        }
        this.cdr.markForCheck();
    }

    private processAccessData(accessData: ServiceAccessResponse[]): void {
        this.accessForm.entries = [];
        this.accessForm.removedEntries = [];

        if (accessData && Array.isArray(accessData)) {
            accessData.forEach(item => {
                if (item.isUserAccess) {
                    let name = item.fullName ?? '';
                    if (!name) {
                        const foundUser = this.availableUsers.find(u => u.id === item.userId);
                        if (foundUser) { name = foundUser.name; }
                        else { name = `User ${item.userId}`; }
                    }
                    this.accessForm.entries.push({
                        id: item.userId ?? '',
                        type: 'user',
                        name,
                        permission: item.permissionId,
                        permissionName: item.permissionName,
                        serviceAccessId: item.serviceAccessId,
                        entitlement: item.entitlement ?? null
                    });
                } else if (item.isGroupAccess) {
                    let name = item.groupName ?? '';
                    if (!name) {
                        const foundGroup = this.availableGroups.find(g => g.id === item.groupId);
                        name = foundGroup ? foundGroup.name : `Group ${item.groupId}`;
                    }
                    this.accessForm.entries.push({
                        id: item.groupId ?? '',
                        type: 'group',
                        name,
                        permission: item.permissionId,
                        permissionName: item.permissionName,
                        serviceAccessId: item.serviceAccessId,
                        entitlement: null
                    });
                }
            });
        }
    }

    private fetchAccessData(): void {
        if (!this.serviceId || !this.hasAccess) return;
        this.isLoadingAccess = true;
        this.statusPageService.getServiceAccess(this.token, this.serviceId, this.entityType).subscribe({
            next: (accessData: ServiceAccessResponse[]) => {
                this.processAccessData(accessData);
                this.isLoadingAccess = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoadingAccess = false;
                this.cdr.detectChanges();
            }
        });
    }

    private reset(): void {
        this.accessForm = { enabled: false, entries: [], removedEntries: [] };
        this.accessModified = false;
        this.availableUsers = [];
        this.availableGroups = [];
        this.activeAccessTab = 'users';
        this.activePermissionTab = 'edit';
        this.searchQueryUsers = '';
        this.searchQueryGroups = '';
        this.isEditingAccess = false;
        this.isEditingEntries = false;
        this.selectedUserIds.clear();
        this.selectedGroupIds.clear();
        this.selectedEntryIds.clear();
        this.isTransferringOwnership = false;
        this.selectedNewOwnerId = null;
        this.pendingOwnerTransfer = null;
    }

    private buildLoadKey(): string {
        return [
            String(this.serviceId ?? ''),
            String(this.token ?? ''),
            this.entityType,
            this.showCustomAccessUi ? '1' : '0'
        ].join('|');
    }

    toggleOwnershipTransfer(): void {
        this.isTransferringOwnership = !this.isTransferringOwnership;
        this.selectedNewOwnerId = null;
        this.searchQueryTransfer = '';
        this.cdr.markForCheck();
    }

    cancelOwnershipTransfer(): void {
        this.isTransferringOwnership = false;
        this.selectedNewOwnerId = null;
        this.searchQueryTransfer = '';
        this.cdr.markForCheck();
    }

    onTransferOwnerEnter(event: KeyboardEvent, userId: string | number): void {
        event.preventDefault();
        event.stopPropagation();
        this.selectedNewOwnerId = userId;
        this.cdr.markForCheck();
    }

    confirmOwnershipTransfer(): void {
        if (!this.selectedNewOwnerId || !this.serviceId || !this.token) return;

        const newOwnerEntry = this.accessForm.entries?.find((e: any) => e.id === this.selectedNewOwnerId && e.type === 'user');
        if (!newOwnerEntry) return;

        const dialogRef = this.layoutUtilsService.submitElement(
            `Are you sure you want to Switch owner to ${newOwnerEntry.name}?`,
            '',
            'Switching owner...'
        );

        dialogRef.afterClosed().subscribe(res => {
            if (!res) return;

            this.isLoadingAccess = true;
            this.cdr.detectChanges();

            const transferOwnership$ = this.statusPageService.transferOwnership(this.token, this.serviceId, newOwnerEntry.id, this.entityType);

            transferOwnership$.subscribe({
                next: () => {
                    this.pendingOwnerTransfer = {
                        id: newOwnerEntry.id,
                        name: newOwnerEntry.name
                    };
                    this.isTransferringOwnership = false;
                    this.selectedNewOwnerId = null;
                    this.searchQueryTransfer = '';
                    this.layoutUtilsService.showActionNotification('Owner switched successfully', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
                    if (this.usesPreloadedPermission()) {
                        this.permissionRefreshRequested.emit();
                        return;
                    }
                    this.loadAccessDependencies(true);
                },
                error: (err) => {
                    const errorMsg = getStatusPageErrorMessage(err, 'Failed to switch owner');
                    this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                }
            });
        });
    }

    private usesPreloadedPermission(): boolean {
        return this.permissionData !== undefined || this.permissionLoading !== null;
    }

    private applyPermissionData(permissionData: ServiceAccessPermission | null): void {
        if (permissionData) {
            this.accessForm.enabled = permissionData.isRestricted === true;
            this.hasAccess = !!permissionData.hasAccess;
            // Name/description edit permission: owner OR edit.
            const canEditServiceDetails = !!(permissionData.isOwner || permissionData.canEdit);
            // Custom Access management: owner only.
            this.canModifyAccess = !!permissionData.isOwner;
            this.editPermissionChange.emit(canEditServiceDetails);

            if (this.hasAccess && this.showCustomAccessUi) {
                this.isLoadingAccess = true;
                this.cdr.detectChanges();
                this.statusPageService.getServiceAccess(this.token, this.serviceId, this.entityType).subscribe({
                    next: (accessData: ServiceAccessResponse[]) => {
                        this.processAccessData(accessData);
                        this.isLoadingAccess = false;
                        this.cdr.detectChanges();
                    },
                    error: () => {
                        this.isLoadingAccess = false;
                        this.cdr.detectChanges();
                    }
                });
            } else {
                this.accessForm.entries = [];
                this.accessForm.removedEntries = [];
                this.isLoadingAccess = false;
                this.cdr.detectChanges();
            }
            return;
        }

        this.hasAccess = false;
        this.canModifyAccess = false;
        this.editPermissionChange.emit(false);
        this.accessForm.entries = [];
        this.accessForm.removedEntries = [];
        this.isLoadingAccess = false;
        this.cdr.detectChanges();
    }

    get filteredUsers(): AvailableUser[] {
        let users = this.availableUsers;

        if (this.activePermissionTab === 'edit') {
            users = users.filter((u: any) => u.entitlement === 'Edit');
        }

        if (this.searchQueryUsers) {
            const q = this.searchQueryUsers.toLowerCase();
            users = users.filter(u => u.name.toLowerCase().includes(q));
        }

        return users;
    }

    get filteredGroups(): AvailableGroup[] {
        if (!this.searchQueryGroups) return this.availableGroups;
        const q = this.searchQueryGroups.toLowerCase();
        return this.availableGroups.filter(g => g.name.toLowerCase().includes(q));
    }

    get ownerEntry() {
        return this.accessForm.entries?.find((e: any) => e.permission === 1);
    }

    get userEntries() {
        return this.accessForm.entries?.filter((e: any) => e.type === 'user') ?? [];
    }

    get groupEntries() {
        return this.accessForm.entries?.filter((e: any) => e.type === 'group') ?? [];
    }

    get activeEntryCount() {
        return this.activeAccessTab === 'users' ? this.userEntries.length : this.groupEntries.length;
    }

    get nonOwnerUserCount() {
        return this.userEntries.filter((e: any) => e.permission !== 1).length;
    }

    get nonOwnerGroupCount() {
        return this.groupEntries.filter((e: any) => e.permission !== 1).length;
    }

    get hasActiveNonOwnerEntries() {
        return this.activeAccessTab === 'users' ? this.nonOwnerUserCount > 0 : this.nonOwnerGroupCount > 0;
    }

    get filteredEntries() {
        const permValue = this.activePermissionTab === 'edit' ? 2 : 3;
        const type = this.activeAccessTab === 'users' ? 'user' : 'group';
        return this.accessForm.entries?.filter((e: any) =>
            e.type === type && e.permission === permValue
        ) ?? [];
    }

    get editTabCount() {
        const type = this.activeAccessTab === 'users' ? 'user' : 'group';
        return this.accessForm.entries?.filter((e: any) => e.type === type && e.permission === 2).length ?? 0;
    }

    get viewTabCount() {
        const type = this.activeAccessTab === 'users' ? 'user' : 'group';
        return this.accessForm.entries?.filter((e: any) => e.type === type && e.permission === 3).length ?? 0;
    }

    get selectedCount(): number {
        return this.activeAccessTab === 'users'
            ? this.selectedUserIds.size
            : this.selectedGroupIds.size;
    }

    get selectedEntryCount(): number { return this.selectedEntryIds.size; }

    get eligibleNewOwners(): any[] {
        return this.accessForm.entries?.filter((e: any) => e.type === 'user' && e.permission === 2) ?? [];
    }

    get filteredEligibleNewOwners(): any[] {
        let users = this.eligibleNewOwners;
        if (this.searchQueryTransfer) {
            const q = this.searchQueryTransfer.toLowerCase();
            users = users.filter((u: any) => u.name.toLowerCase().includes(q));
        }
        return users;
    }

    trackByEligibleOwnerId(index: number, user: { id?: string | number } | null | undefined): string {
        const id = String(user?.id ?? '').trim();
        if (id) {
            return `owner:${id}`;
        }
        return `owner-idx:${index}`;
    }

    trackByAvailableUserId(index: number, user: AvailableUser | null | undefined): string {
        const id = String(user?.id ?? '').trim();
        if (id) {
            return `user:${id}`;
        }
        return `user-idx:${index}`;
    }

    trackByAvailableGroupId(index: number, group: AvailableGroup | null | undefined): string {
        const id = String(group?.id ?? '').trim();
        if (id) {
            return `group:${id}`;
        }
        return `group-idx:${index}`;
    }

    trackByAccessEntryId(index: number, entry: AccessEntry | null | undefined): string {
        const type = String(entry?.type ?? '').trim();
        const id = String(entry?.id ?? '').trim();
        if (type && id) {
            return `entry:${type}:${id}`;
        }
        return `entry-idx:${index}`;
    }

    get canSwitchSelectedToEdit(): boolean {
        if (this.selectedEntryIds.size === 0) return false;
        const selectedEntries = this.filteredEntries.filter(e => this.selectedEntryIds.has(e.id));
        return selectedEntries.every(e => e.type === 'group' || e.entitlement === 'Edit');
    }

    get isAllUsersSelected(): boolean {
        return this.filteredUsers.length > 0 && this.selectedUserIds.size === this.filteredUsers.length;
    }

    get isAllGroupsSelected(): boolean {
        return this.filteredGroups.length > 0 && this.selectedGroupIds.size === this.filteredGroups.length;
    }

    get isAllEntriesSelected(): boolean {
        const editable = this.filteredEntries.filter(e => e.permission !== 1);
        return editable.length > 0 && this.selectedEntryIds.size === editable.length;
    }

    onAccessControlToggle(enabled: boolean): void {
        this.accessModified = true;
        if (!this.serviceId || !this.hasAccess) return;

        this.isLoadingAccess = true;

        if (enabled) {
            this.statusPageService.restrictServiceAccess(this.token, this.serviceId, this.entityType).subscribe({
                next: () => {
                    this.fetchAccessData();
                },
                error: () => {
                    this.accessForm.enabled = false;
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                }
            });
        } else {
            this.statusPageService.unrestrictServiceAccess(this.token, this.serviceId, this.entityType).subscribe({
                next: () => {
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.accessForm.enabled = true;
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                }
            });
        }
    }

    onAccessControlToggleEnter(event: KeyboardEvent): void {
        if (!this.canModifyAccess || this.readonly) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const nextState = !this.accessForm.enabled;
        this.accessForm.enabled = nextState;
        this.onAccessControlToggle(nextState);
        this.cdr.markForCheck();
    }

    switchEntityTab(tab: 'users' | 'groups'): void {
        if (this.activeAccessTab === tab) return;
        this.activeAccessTab = tab;
        this.selectedUserIds.clear();
        this.selectedGroupIds.clear();
        this.searchQueryUsers = '';
        this.searchQueryGroups = '';
        this.cdr.markForCheck();
    }

    onSearchUsers(query: string): void {
        this.searchQueryUsers = query;
        this.cdr.markForCheck();
    }

    onSearchGroups(query: string): void {
        this.searchQueryGroups = query;
        this.cdr.markForCheck();
    }

    switchPermissionTab(tab: 'edit' | 'view'): void {
        if (this.activePermissionTab === tab) return;
        this.activePermissionTab = tab;
        this.selectedUserIds.clear();
        this.selectedGroupIds.clear();
        this.cdr.markForCheck();
    }

    toggleUserSelection(id: string | number): void {
        if (this.selectedUserIds.has(id)) this.selectedUserIds.delete(id);
        else this.selectedUserIds.add(id);
        this.cdr.markForCheck();
    }

    toggleGroupSelection(id: string | number): void {
        if (this.selectedGroupIds.has(id)) this.selectedGroupIds.delete(id);
        else this.selectedGroupIds.add(id);
        this.cdr.markForCheck();
    }

    onEntityRowEnter(type: 'user' | 'group', id: string | number): void {
        if (this.checkboxOnlyEntityTabFocus) {
            return;
        }
        this.toggleEntityByType(type, id);
    }

    onEntityRowSpace(event: KeyboardEvent, type: 'user' | 'group', id: string | number): void {
        if (this.checkboxOnlyEntityTabFocus) {
            return;
        }
        this.toggleEntityByType(type, id);
        event.preventDefault();
    }

    onEntityCheckboxClick(event: Event): void {
        if (this.checkboxOnlyEntityTabFocus) {
            event.stopPropagation();
        }
    }

    onEntityCheckboxEnter(event: KeyboardEvent, type: 'user' | 'group', id: string | number): void {
        if (!this.checkboxOnlyEntityTabFocus) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.toggleEntityByType(type, id);
    }

    onUserCheckboxSelectionChange(id: string | number, checked: boolean): void {
        if (!this.checkboxOnlyEntityTabFocus) {
            return;
        }
        this.setEntitySelection(this.selectedUserIds, id, checked);
    }

    onGroupCheckboxSelectionChange(id: string | number, checked: boolean): void {
        if (!this.checkboxOnlyEntityTabFocus) {
            return;
        }
        this.setEntitySelection(this.selectedGroupIds, id, checked);
    }

    toggleAllUsers(): void {
        if (this.isAllUsersSelected) this.selectedUserIds.clear();
        else this.filteredUsers.forEach(u => this.selectedUserIds.add(u.id));
        this.cdr.markForCheck();
    }

    toggleAllGroups(): void {
        if (this.isAllGroupsSelected) this.selectedGroupIds.clear();
        else this.filteredGroups.forEach(g => this.selectedGroupIds.add(g.id));
        this.cdr.markForCheck();
    }

    onAddPanelSelectAllEnter(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.activeAccessTab === 'users') {
            this.toggleAllUsers();
            return;
        }
        this.toggleAllGroups();
    }

    private setEntitySelection(targetSet: Set<string | number>, id: string | number, checked: boolean): void {
        if (checked) {
            targetSet.add(id);
        } else {
            targetSet.delete(id);
        }
        this.cdr.markForCheck();
    }

    private toggleEntityByType(type: 'user' | 'group', id: string | number): void {
        if (type === 'user') {
            this.toggleUserSelection(id);
            return;
        }
        this.toggleGroupSelection(id);
    }

    addSelected(): void {
        const permission = this.activePermissionTab === 'edit' ? 2 : 3;
        const grants: any[] = [];

        if (this.activeAccessTab === 'users') {
            this.selectedUserIds.forEach(uid => grants.push({ userId: Number(uid), permissionId: permission }));
        } else {
            this.selectedGroupIds.forEach(gid => grants.push({ groupId: Number(gid), permissionId: permission }));
        }

        if (grants.length === 0) return;

        this.isLoadingAccess = true;
        this.statusPageService.addServiceAccessBatch(this.token, this.serviceId, { grants }, this.entityType).subscribe({
            next: () => {
                this.layoutUtilsService.showActionNotification('Access added successfully', MessageType.Create, environment.TOAST_MESSAGE_TIME, true, false);
                this.selectedUserIds.clear();
                this.selectedGroupIds.clear();
                this.isEditingAccess = false;
                this.fetchAccessData();
            },
            error: (err) => {
                const errorMsg = getStatusPageErrorMessage(err, 'Failed to add access');
                this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
                this.isLoadingAccess = false;
                this.cdr.detectChanges();
            }
        });
    }

    hasUserAccess(userId: string | number): boolean {
        return this.accessForm.entries.some(e => e.type === 'user' && e.id === userId);
    }

    hasGroupAccess(groupId: string | number): boolean {
        return this.accessForm.entries.some(e => e.type === 'group' && e.id === groupId);
    }

    getUserPermission(userId: string | number): number {
        const entry = this.accessForm.entries.find(e => e.type === 'user' && e.id === userId);
        return entry ? Number(entry.permission) : 2;
    }

    getGroupPermission(groupId: string | number): number {
        const entry = this.accessForm.entries.find(e => e.type === 'group' && e.id === groupId);
        return entry ? Number(entry.permission) : 2;
    }

    updateUserPermission(userId: string | number, permission: number): void {
        const entry = this.accessForm.entries.find(e => e.type === 'user' && e.id === userId);
        if (entry) {
            entry.permission = permission;
            entry.permissionName = this.permissionOptions.find(p => p.value === permission)?.label || 'Edit';
            this.accessModified = true;
        }
    }

    updateGroupPermission(groupId: string | number, permission: number): void {
        const entry = this.accessForm.entries.find(e => e.type === 'group' && e.id === groupId);
        if (entry) {
            entry.permission = permission;
            entry.permissionName = this.permissionOptions.find(p => p.value === permission)?.label || 'Edit';
            this.accessModified = true;
        }
    }

    updatePermission(entry: AccessEntry, permissionId: number): void {
        entry.permission = permissionId;
        if (permissionId === 1) entry.permissionName = 'Owner';
        else if (permissionId === 2) entry.permissionName = 'Edit';
        else if (permissionId === 3) entry.permissionName = 'View';
        this.accessModified = true;
    }

    removeAccess(entry: AccessEntry): void {
        const dialogRef = this.layoutUtilsService.deleteElement(
            `Remove ${entry.type === 'user' ? 'User' : 'Group'} Access`,
            `Are you sure you want to remove access for "${entry.name}"?`,
            'Removing access...'
        );

        dialogRef.afterClosed().subscribe(res => {
            if (!res) return;

            const revocations: any[] = [];
            if (entry.type === 'user') {
                revocations.push({ userId: Number(entry.id) });
            } else {
                revocations.push({ groupId: Number(entry.id) });
            }

            this.isLoadingAccess = true;
            this.cdr.detectChanges();

            this.statusPageService.removeServiceAccessBatch(this.token, this.serviceId, { revocations }, this.entityType).subscribe({
                next: () => {
                    this.layoutUtilsService.showActionNotification('Access removed successfully', MessageType.Delete, environment.TOAST_MESSAGE_TIME, true, false);
                    this.fetchAccessData();
                },
                error: (error) => {
                    const errorMsg = getStatusPageErrorMessage(error, 'Failed to remove access');
                    this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                }
            });
        });
    }

    toggleEntrySelection(id: string | number): void {
        if (this.selectedEntryIds.has(id)) this.selectedEntryIds.delete(id);
        else this.selectedEntryIds.add(id);
        this.cdr.markForCheck();
    }

    toggleAllEntries(): void {
        const editable = this.filteredEntries.filter(e => e.permission !== 1);
        if (this.isAllEntriesSelected) this.selectedEntryIds.clear();
        else editable.forEach(e => this.selectedEntryIds.add(e.id));
        this.cdr.markForCheck();
    }

    onEntriesSelectAllEnter(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.toggleAllEntries();
    }

    cancelEditMode(): void {
        this.isEditingEntries = false;
        this.selectedEntryIds.clear();
        this.cdr.markForCheck();
    }

    deleteSelected(): void {
        const count = this.selectedEntryIds.size;
        const dialogRef = this.layoutUtilsService.deleteElement(
            'Remove Selected Access',
            `Are you sure you want to remove access for ${count} selected ${count === 1 ? 'entry' : 'entries'}?`,
            'Removing access...'
        );

        dialogRef.afterClosed().subscribe(res => {
            if (!res) return;

            const revocations: any[] = [];
            this.selectedEntryIds.forEach(id => {
                if (this.activeAccessTab === 'users') revocations.push({ userId: Number(id) });
                else revocations.push({ groupId: Number(id) });
            });

            if (revocations.length === 0) return;

            this.isLoadingAccess = true;
            this.cdr.detectChanges();

            this.statusPageService.removeServiceAccessBatch(this.token, this.serviceId, { revocations }, this.entityType).subscribe({
                next: () => {
                    this.layoutUtilsService.showActionNotification('Access removed successfully', MessageType.Delete, environment.TOAST_MESSAGE_TIME, true, false);
                    this.selectedEntryIds.clear();
                    this.isEditingEntries = false;
                    this.fetchAccessData();
                },
                error: (error) => {
                    const errorMsg = getStatusPageErrorMessage(error, 'Failed to remove access');
                    this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
                    this.isLoadingAccess = false;
                    this.cdr.detectChanges();
                }
            });
        });
    }

    switchSelectedTo(perm: 'edit' | 'view'): void {
        const permValue = perm === 'edit' ? 2 : 3;
        const grants: any[] = [];

        this.selectedEntryIds.forEach(id => {
            if (this.activeAccessTab === 'users') grants.push({ userId: Number(id), permissionId: permValue });
            else grants.push({ groupId: Number(id), permissionId: permValue });
        });

        if (grants.length === 0) return;

        this.isLoadingAccess = true;
        this.statusPageService.updateServiceAccessBatch(this.token, this.serviceId, { grants }, this.entityType).subscribe({
            next: () => {
                this.layoutUtilsService.showActionNotification('Access updated successfully', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
                this.selectedEntryIds.clear();
                this.isEditingEntries = false;
                this.fetchAccessData();
            },
            error: (error) => {
                const errorMsg = getStatusPageErrorMessage(error, 'Failed to update access');
                this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
                this.isLoadingAccess = false;
                this.cdr.detectChanges();
            }
        });
    }

    toggleUserAccess(user: AvailableUser, checked: boolean): void {
        const index = this.accessForm.entries.findIndex(e => e.type === 'user' && e.id === user.id);
        if (checked && index === -1) {
            this.accessForm.entries.push({
                id: user.id, type: 'user', name: user.name,
                permission: 2, permissionName: 'Edit'
            });
            this.accessModified = true;
        } else if (!checked && index !== -1) {
            const removed = this.accessForm.entries.splice(index, 1)[0];
            if (removed.serviceAccessId) {
                this.accessForm.removedEntries = this.accessForm.removedEntries || [];
                this.accessForm.removedEntries.push(removed);
            }
            this.accessModified = true;
        }
    }

    toggleGroupAccess(group: AvailableGroup, checked: boolean): void {
        const index = this.accessForm.entries.findIndex(e => e.type === 'group' && e.id === group.id);
        if (checked && index === -1) {
            this.accessForm.entries.push({
                id: group.id, type: 'group', name: group.name, permission: 2, permissionName: 'Edit'
            });
            this.accessModified = true;
        } else if (!checked && index !== -1) {
            const removed = this.accessForm.entries.splice(index, 1)[0];
            if (removed.serviceAccessId) {
                this.accessForm.removedEntries = this.accessForm.removedEntries || [];
                this.accessForm.removedEntries.push(removed);
            }
            this.accessModified = true;
        }
    }
}
